import React, { useCallback, useMemo } from "react";
import sortBy from "lodash/sortBy";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { useQuery } from "@apollo/client/react";

import useMutation from "src/hooks/useMutation";
import { PARTIAL_TRANSFER_SHIPMENT_MUTATION } from "src/constants/graphql/mutations";
import { CLIENT_PROJECT_OPTIONS_QUERY } from "src/constants/graphql/queries";
import EntitySelect from "src/components/EntitySelect";
import PdfLinkActions from "src/components/PdfLinkActions";
import useNotification from "src/hooks/notification";
import useAuth from "src/hooks/useAuth";
import Header from "src/components/wizard/Header";
import Footer from "src/components/wizard/Footer/Footer";
import {
  buildDuplicateProjectNameCounts,
  getProjectClientLabel,
  getProjectOptionLabel,
} from "src/utils/projectOptionLabels";
import { getActiveProjectsForClients } from "src/utils/clientProjectOptions";

export default function Step3BDestinationProject({
  shipment,
  step,
  totalSteps,
  selectedItemIds,
  selectedClient,
  setSelectedClient,
  selectedProject,
  setSelectedProject,
  onBack,
  onTransferSuccess,
  onClose,
}) {
  const { notifySuccess, notifyError } = useNotification();
  const { user } = useAuth();
  const { settings } = user.company;
  const filterField2 = settings?.filter_field2;
  const warehouseDropdownEnabled = settings.warehouse_id_dropdown_menu;
  const showClientNameInProjectList = Boolean(settings.display_client_name_email_notification);
  const [partialTransferShipment, { loading }] = useMutation(PARTIAL_TRANSFER_SHIPMENT_MUTATION);
  const { data: { clients = [] } = {} } = useQuery(CLIENT_PROJECT_OPTIONS_QUERY);

  const clientList = sortBy(
    clients
      .filter(client => !client.disabled)
      .map(({ id, name }) => ({ id, name }))
      .filter(v => user.clientIds.includes(v.id)),
    "name"
  );

  const projectList = useMemo(
    () => getActiveProjectsForClients(clients, !warehouseDropdownEnabled ? selectedClient : null),
    [clients, selectedClient, warehouseDropdownEnabled]
  );
  const duplicateNameCounts = useMemo(
    () => buildDuplicateProjectNameCounts(projectList),
    [projectList]
  );

  const handlePartialTransfer = useCallback(() => {
    if (!selectedProject) {
      notifyError("Please select a project");
      return;
    }

    partialTransferShipment({
      variables: {
        shipmentId: shipment?.id,
        projectId: selectedProject,
        shipmentItemIds: selectedItemIds,
      },
      onSuccess: data => {
        const newShipmentId = data.newShipment?.shipmentId;
        notifySuccess(`Partial transfer complete. New shipment ${newShipmentId} created.`);
        onTransferSuccess(data.newShipment);
      },
      onFailure: () => {
        notifyError("Something went wrong during partial transfer");
      },
    });
  }, [
    shipment,
    selectedProject,
    selectedItemIds,
    partialTransferShipment,
    onTransferSuccess,
    notifyError,
    notifySuccess,
  ]);

  const shipmentInfo = (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography>{shipment?.shipmentId}</Typography>
      <PdfLinkActions
        resourceType="shipment_pdfs"
        resourceId={shipment?.id}
        sx={{
          textTransform: "none",
          fontSize: 12,
          py: 0.2,
          px: 1,
          minWidth: "auto",
          height: 25,
          borderRadius: 10,
        }}
      />
    </Box>
  );

  return (
    <>
      <Header title={`Select Destination ${filterField2}`} info={shipmentInfo} />

      <Box
        p={2}
        minHeight={400}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            The selected items will be moved to a new inbound shipment.
            <br />
            <Typography variant="body2" component="span" sx={{ fontStyle: "italic" }}>
              You can select the same {filterField2?.toLowerCase() || "project"} if items belong to
              different cost centers or billing types.
            </Typography>
          </Alert>

          <Grid container spacing={2}>
            {!warehouseDropdownEnabled && (
              <Grid size={{ xs: 12, md: 6 }}>
                <EntitySelect
                  fullWidth
                  label={settings?.filter_field1}
                  value={selectedClient}
                  suggestions={clientList}
                  onChange={clientItem => {
                    const id = clientItem?.id ? clientItem.id : null;
                    setSelectedClient(id);
                    setSelectedProject(null);
                  }}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: warehouseDropdownEnabled ? 12 : 6 }}>
              <EntitySelect
                fullWidth
                label={filterField2}
                value={selectedProject}
                suggestions={projectList}
                getOptionLabel={option =>
                  getProjectOptionLabel(
                    option,
                    duplicateNameCounts,
                    showClientNameInProjectList
                  )
                }
                renderOption={(props, option) => {
                  const optionProps = { ...props };
                  delete optionProps.key;
                  const clientLabel = getProjectClientLabel(
                    option,
                    duplicateNameCounts,
                    showClientNameInProjectList
                  );
                  return (
                    <li {...optionProps} key={option.id}>
                      {option.name}
                      {clientLabel ? (
                        <Typography component="span" ml={0.5} color="text.secondary" fontSize={14}>
                          {" - "}
                          {clientLabel}
                        </Typography>
                      ) : null}
                    </li>
                  );
                }}
                onChange={projectItem => {
                  setSelectedProject(projectItem.id);
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Footer step={step} steps={totalSteps} showRaceMode={false}>
        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={onBack}>
            Back
          </Button>
          <Button
            disabled={!selectedProject || loading}
            variant="contained"
            color="primary"
            onClick={handlePartialTransfer}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Transfer
          </Button>
        </Box>
      </Footer>
    </>
  );
}
